// ---------------------------------------------------------------------------
// The bell in the header.
//
// Two queries, deliberately split: the COUNT is polled every couple of
// minutes; the LIST is fetched only when the panel is open. On a till that
// stays open all day, polling the whole list would be a steady drip of
// pointless work.
//
// The panel is filled entirely by the server. A cashier's bell is not the
// owner's list with rows hidden - the query returns different rows, because
// each notification carries the permission needed to see it. So there is no
// permission logic in this file at all, and that is the point.
//
// The panel itself is drawn by `HeaderPopover`, which portals it to the body.
// It used to be absolutely positioned inside the header, and the header is
// `overflow-hidden` - which clips a descendant no matter how high its z-index
// is. That is why the list appeared to open behind the page.
// ---------------------------------------------------------------------------

import { useCallback, useId, useRef, useState } from 'react';
import { Bell, Check, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { usePermission } from '../../hooks/usePermissions';
import {
  useAcknowledgeAllNotifications,
  useAcknowledgeNotification,
  useNotificationCounts,
  useNotifications,
  useSweepNotifications,
} from '../../api/alerts.api';
import { formatRelative } from '../../utils/format';
import { HeaderPopover } from './HeaderPopover';
import type { ShopNotification } from '../../types';

export function NotificationBell() {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [showSeen, setShowSeen] = useState(false);

  /// The bell. The panel is placed from this button's own rectangle.
  const bellRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  // Stable, so opening the panel does not immediately re-register it in the
  // one-at-a-time registry and close itself.
  const close = useCallback(() => setOpen(false), []);

  const counts = useNotificationCounts();
  const list = useNotifications({ includeAcknowledged: showSeen }, open);
  const acknowledge = useAcknowledgeNotification();
  const acknowledgeAll = useAcknowledgeAllNotifications();
  const sweep = useSweepNotifications();

  // Re-deriving is a full pass over stock, batches and invoices, so the button
  // is only offered to somebody who may read inventory - the same permission
  // the endpoint requires.
  const canSweep = usePermission('inventory.view');

  const total = counts.data?.total ?? 0;
  const urgent = (counts.data?.CRITICAL ?? 0) > 0;

  return (
    <>
      <button
        ref={bellRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={t.notifications.open}
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-control text-ink-muted transition-colors hover:bg-surface-soft hover:text-ink"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />

        {total > 0 ? (
          <span
            className={clsx(
              'absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white',
              urgent ? 'bg-danger' : 'bg-antique-gold',
            )}
          >
            {total > 99 ? '99+' : total}
          </span>
        ) : null}
      </button>

      <HeaderPopover
        id="notifications"
        open={open}
        onClose={close}
        anchorRef={bellRef}
        labelledBy={titleId}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-3">
            <h3 id={titleId} className="text-sm font-semibold text-ink">
              {t.notifications.title}
            </h3>

            <div className="flex items-center gap-1">
              {canSweep ? (
                <button
                  type="button"
                  className="rounded-control p-1.5 text-ink-muted transition-colors hover:bg-surface-soft hover:text-ink"
                  title={t.notifications.refresh}
                  disabled={sweep.isPending}
                  onClick={() => sweep.mutate()}
                >
                  <RefreshCw
                    className={clsx('h-4 w-4', sweep.isPending && 'animate-spin')}
                    aria-hidden="true"
                  />
                  <span className="sr-only">{t.notifications.refresh}</span>
                </button>
              ) : null}

              {total > 0 ? (
                <button
                  type="button"
                  className="rounded-control p-1.5 text-ink-muted transition-colors hover:bg-surface-soft hover:text-ink"
                  title={t.notifications.acknowledgeAll}
                  disabled={acknowledgeAll.isPending}
                  onClick={() => acknowledgeAll.mutate()}
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">{t.notifications.acknowledgeAll}</span>
                </button>
              ) : null}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.isLoading ? (
              <p className="px-4 py-6 text-center text-sm text-ink-muted">{t.common.loading}</p>
            ) : (list.data ?? []).length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-medium text-ink">{t.notifications.empty}</p>
                <p className="mt-1 text-xs text-ink-muted">{t.notifications.emptyHint}</p>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {(list.data ?? []).map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    locale={locale}
                    onAcknowledge={() => acknowledge.mutate(notification.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          <label className="flex shrink-0 items-center gap-2 border-t border-line px-4 py-2.5 text-xs text-ink-muted">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-line"
              checked={showSeen}
              onChange={(event) => setShowSeen(event.target.checked)}
            />
            {t.notifications.showAcknowledged}
          </label>
      </HeaderPopover>
    </>
  );
}

function NotificationRow({
  notification,
  locale,
  onAcknowledge,
}: {
  notification: ShopNotification;
  locale: 'en' | 'ar';
  onAcknowledge: () => void;
}) {
  const { t } = useI18n();
  const seen = notification.acknowledgedAt !== null;

  return (
    <li className={clsx('px-4 py-3', seen && 'opacity-60')}>
      <div className="flex items-start gap-2.5">
        <span
          className={clsx(
            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
            notification.severity === 'CRITICAL' && 'bg-danger',
            notification.severity === 'WARNING' && 'bg-antique-gold',
            notification.severity === 'INFO' && 'bg-ink-muted',
          )}
          aria-label={t.notifications.severity[notification.severity]}
        />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink">
            {locale === 'ar' ? notification.titleAr : notification.titleEn}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
            {locale === 'ar' ? notification.bodyAr : notification.bodyEn}
          </p>
          <p className="mt-1 text-[11px] text-ink-muted">
            {t.notifications.kinds[notification.kind]} · {formatRelative(notification.createdAt, locale)}
          </p>
        </div>

        {!seen ? (
          <button
            type="button"
            onClick={onAcknowledge}
            title={t.notifications.acknowledge}
            className="shrink-0 rounded-control p-1 text-ink-muted transition-colors hover:bg-surface-soft hover:text-butcher-green"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{t.notifications.acknowledge}</span>
          </button>
        ) : null}
      </div>
    </li>
  );
}
